import { useTranslation } from 'react-i18next'
import { Trash2 } from 'lucide-react'
import type { Control, FieldErrors } from 'react-hook-form'
import { useController } from 'react-hook-form'
import { IngredientAutocomplete } from './IngredientAutocomplete'
import type { RecipeFormData } from './types'

interface IngredientRowProps {
  index: number
  onRemove: (index: number) => void
  control: Control<RecipeFormData>
  errors: FieldErrors<RecipeFormData>
}

export function IngredientRow({ index, onRemove, control, errors }: IngredientRowProps) {
  const { t } = useTranslation('recipes')
  const { field } = useController({ control, name: `ingredients.${index}` })
  const ingredientErrors = errors.ingredients?.[index]

  return (
    <div className="flex items-start gap-2 py-2">
      <IngredientAutocomplete
        value={field.value}
        onChange={field.onChange}
        index={index}
      />
      <input
        type="number"
        step="any"
        value={field.value.quantity || ''}
        onChange={(e) => field.onChange({ ...field.value, quantity: parseFloat(e.target.value) || 0 })}
        placeholder={t('form.quantityPlaceholder')}
        aria-label={t('form.quantityLabel')}
        className="w-20 rounded-lg border border-line bg-surface px-2 py-2 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-accent min-h-[44px]"
      />
      <input
        type="text"
        value={field.value.unit}
        onChange={(e) => field.onChange({ ...field.value, unit: e.target.value })}
        placeholder={t('form.unitPlaceholder')}
        aria-label={t('form.unitLabel')}
        className="w-20 rounded-lg border border-line bg-surface px-2 py-2 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-accent min-h-[44px]"
      />
      <button
        type="button"
        onClick={() => onRemove(index)}
        aria-label={t('form.removeIngredientAriaLabel', { index: index + 1 })}
        className="flex items-center justify-center min-h-[44px] min-w-[44px] text-danger hover:bg-red-50 rounded-lg"
      >
        <Trash2 className="h-4 w-4" aria-hidden="true" />
      </button>

      {ingredientErrors && (
        <div className="col-span-full">
          {ingredientErrors.ingredientName && (
            <p role="alert" className="text-xs text-danger">
              {ingredientErrors.ingredientName.message}
            </p>
          )}
          {ingredientErrors.quantity && (
            <p role="alert" className="text-xs text-danger">
              {ingredientErrors.quantity.message}
            </p>
          )}
        </div>
      )}
    </div>
  )
}
